import ConfigurationFormEditContext from "@/contexts/forms/ConfigurationFormEditContext";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faArrowsRotate, faChevronDown, faEarthOceania, faEdit, faEye, faLinkHorizontal, faLinkHorizontalSlash, faSlash } from "@fortawesome/duotone-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { alpha, Box, Chip, CircularProgress, Divider, IconButton, ListItem, ListItemText, styled, Tooltip, Typography } from "@mui/material";
import { Checkbox, CollapsiblePanel, ICollapsiblePanelComponentProps, InputOnlyField } from "@ngt/forms";
import { useFormActions, useScopedField } from "@ngt/forms-core";
import { FC, ReactNode, useCallback, useContext, useMemo, useTransition } from "react";
import { ISourceDataConfiguration, IValidationError, ISourceDataConfigurationEvent, ISourceDataConfigurationForm, ISourceDataConfigurationFormVersion, ISourceDataConfigurationFormSection, ISourceDataConfigurationFormField, ISourceDataConfigurationFormFieldValue } from '../../../api/dtos';
import { cloneDeep } from 'lodash-es'
import { ConfigurationType, IConfigurationItem } from "@/utilities/forms/ConfigurationFormProcessor";


const FlexBox = styled(Box)({
    display: 'flex',
    alignItems: 'center'
});

const DescriptionChip = styled(Chip)({
    marginLeft: 'auto'
});


const LinkedProgress = styled(CircularProgress)(({linked, theme}: any) => ({
    marginLeft: 3,
    marginTop: 1
}));


const LinkButton = styled(IconButton)(({auto, theme}: any) => ({
    marginLeft: auto ? 'auto' : theme.spacing(2)
}));

const IconBox = styled(Box)(({theme}) => ({
    fontSize: '1.5rem',
    marginRight: theme.spacing(2),
    color: theme.palette.secondary.main
}));

const IconStack = styled(Box)(({theme}) => ({
    position: 'relative'
}));

const IconStacked = styled(Box)(({theme}) => ({
    position: 'absolute'
}));

const IconBase = styled(Box)(({linked, theme}: any) => ({
    opacity: linked ? undefined : 0.5
}));

const EditButton = styled(IconButton)(({auto, theme}: any) => ({
    marginLeft: auto ? 'auto' : theme.spacing(2)
}));

const ExpandButton = styled(IconButton)(({auto, expanded, theme}: any) => ({
    marginLeft: auto ? 'auto' : theme.spacing(2),
    transform: expanded ?
        'rotate(180deg)':
        undefined,
    transition: 'transform 0.25s linear'
}));

const Container = styled(Box)(({ spacing, theme }: any) => ({
    background: theme.palette.background.paper,
    backgroundImage: theme.palette.mode === 'dark' ? 
        `linear-gradient(${alpha(theme.palette.common.white, 0.05)}, ${alpha(theme.palette.common.white, 0.05)})` :
        undefined,
    marginTop: -1,

    marginLeft: spacing ? theme.spacing(spacing) : undefined,
    borderLeft: spacing && spacing > 0 ?
        theme.palette.mode === 'dark' ? 
            `solid 1px ${alpha(theme.palette.common.white, 0.12)}` :
            `solid 1px ${alpha(theme.palette.common.black, 0.12)}` :
        undefined
}));

// export const isChildEditContext = (parent?: IConfigurationFormEditContext, child?: IConfigurationFormEditContext) => {
//     if (parent === child) {
//         return false;
//     }

//     if (parent?.isGlobal !== child?.isGlobal) {
//         return false;
//     }

//     if (parent?.event?.id === child?.event?.id &&
//         parent?.form?.id === child?.form?.id &&
//         parent?.version?.id === child?.version?.id &&
//         parent?.section?.id === child?.section?.id &&
//         parent?.field?.id === child?.field?.id) {
//         return true;
//     }

//     return false;
// };

// export const isEqualEditContext = (editContextA?: IConfigurationFormEditContext, editContextB?: IConfigurationFormEditContext) => {
//     if (editContextA === editContextB) {
//         return true;
//     }

//     if (editContextA?.isGlobal !== editContextB?.isGlobal) {
//         return false;
//     }

//     if (editContextA?.event?.id === editContextB?.event?.id &&
//         editContextA?.form?.id === editContextB?.form?.id &&
//         editContextA?.version?.id === editContextB?.version?.id &&
//         editContextA?.section?.id === editContextB?.section?.id &&
//         editContextA?.field?.id === editContextB?.field?.id) {
//         return true;
//     }

//     return false;
// };


export interface IConfigurationExpandProps {
    setExpanded: (value: boolean, collapseChildren?: boolean) => void;
    toggleExpand: (collapseChildren?: boolean) => void;
    expanded: boolean;
}

export interface IConfigurationItemProps extends IConfigurationExpandProps {
    spacing?: number;
    type: string;
    typeIcon: IconProp;
    basePropertyPath: string;
    title: string;
    description?: string;
    edit?: boolean;
    enableable?: boolean;
    expandable?: boolean;
    trialLinkable?: boolean;
    linkable?: boolean;
    linkedProperties?: string[];
    item?: IConfigurationItem;
}

type ISourceDataConfigurationItem = ISourceDataConfigurationEvent | ISourceDataConfigurationForm | ISourceDataConfigurationFormVersion | ISourceDataConfigurationFormSection | ISourceDataConfigurationFormField | ISourceDataConfigurationFormFieldValue;

const ConfigurationItem: FC<IConfigurationItemProps> = ({
    enableable,
    basePropertyPath,
    type,
    typeIcon,
    title,
    description,
    linkable,
    edit,
    expandable,
    linkedProperties,
    item,
    spacing,
    trialLinkable,
    expanded,
    toggleExpand,
    setExpanded
}) => {
    
    const editContextObserver = useContext(ConfigurationFormEditContext);

    const { getFieldValue, setFieldValue } = useFormActions();

    const { state: { value: linked }, context: { setValue: setLinked } } = useScopedField<boolean, any, IValidationError>(`${basePropertyPath}.linked`, { value: true });

    const { state: { value: localConfiguration }} = useScopedField<ISourceDataConfigurationItem, any, IValidationError>(basePropertyPath, { value: true });

    const globalConfigurationPropertyPath = useMemo(() => {
        if (item?.isGlobalConfiguration || item?.isTrialConfiguration || item?.type === ConfigurationType.Event) {
            return basePropertyPath;
        }

        return `configuration.${basePropertyPath.substring(22 + (item?.eventKey?.length ?? 0))}`;
    }, [basePropertyPath, item?.isGlobalConfiguration, item?.isTrialConfiguration, item?.eventKey, item?.type])

    const { state: { value: globalConfiguration }} = useScopedField<ISourceDataConfigurationItem, any, IValidationError>(globalConfigurationPropertyPath, { value: true });

    const trialConfigurationPropertyPath = useMemo(() => {
        if (item?.isTrialConfiguration) {
            return globalConfigurationPropertyPath;
        }

        return `trialConfiguration.${globalConfigurationPropertyPath.substring(14)}`;
    }, [globalConfigurationPropertyPath, item?.isTrialConfiguration]);

    const { state: { value: trialConfiguration }} = useScopedField<ISourceDataConfigurationItem, any, IValidationError>(trialConfigurationPropertyPath, { value: true });

    const data = useMemo(() => {
        const canExpand = item?.isGlobalConfiguration ||
            item?.type === ConfigurationType.Event ||
            (localConfiguration !== globalConfiguration && localConfiguration?.linked != true)


        if ((localConfiguration !== globalConfiguration || item?.type === ConfigurationType.Event) && !!localConfiguration && !localConfiguration?.linked) {
            return {
                configuration: localConfiguration,
                configurationPropetyPath: basePropertyPath,
                usingGlobalConfiguration: false,
                usingTrialConfiguration: false,
                isGlobalLinked: !item?.isTrialConfiguration && (
                    globalConfiguration?.linked || 
                    item?.type === ConfigurationType.Event || 
                    (item?.isGlobalConfiguration && item.type === ConfigurationType.Form)
                ),
                canExpand
            }
        }
        else if (!!globalConfiguration && !globalConfiguration.linked) {
            return {
                configuration: globalConfiguration,
                configurationPropetyPath: globalConfigurationPropertyPath,
                usingGlobalConfiguration: true,
                usingTrialConfiguration: false,
                isGlobalLinked: globalConfiguration?.linked || item?.type === ConfigurationType.Event,
                canExpand
            }
        }
        else {
            return {
                configuration: trialConfiguration,
                configurationPropetyPath: trialConfigurationPropertyPath,
                usingGlobalConfiguration: false,
                usingTrialConfiguration: true,
                isGlobalLinked: globalConfiguration?.linked,
                canExpand
            }
        }
    }, [trialConfiguration, trialConfigurationPropertyPath, globalConfiguration, globalConfigurationPropertyPath, basePropertyPath, localConfiguration, item?.type, item?.isGlobalConfiguration])    

    const toggle = toggleExpand;

    const overrideName = data.configuration?.name;

    const [isLinking, startLinking] = useTransition();

    const link = () => {
        const currentValue = getFieldValue<any>(basePropertyPath);
        
        const newLinked = !currentValue.linked;
        setLinked(newLinked, true, true, true, true);

        // if the item is no longer linked and this is not the trail configuration being edited.
        if (!newLinked && !item?.isTrialConfiguration) {
            //Get the unique fields from the current item.
            const { 
                __type: currentType, 
                enteredBy: currentEnteredBy,
                enteredDate: currentEnteredDate,
                modifiedBy: currentModifiedBy,
                modifiedDate: currentModifiedDate,
                sourceDataConfigurationId: currentConfigurationId,
                sourceDataConfigurationEventId: currentEventId,
                sourceDataConfigurationFormId: currentFormId,
                sourceDataConfigurationFormVersionId: currentVersionId,
                sourceDataConfigurationFormSectionId: currentSectionId,
                sourceDataConfigurationFormFieldId: currentFieldId,
                eventMetadataId: currentEventMetadataId,
                formMetadataId: currentFormMetadataId,
                formVersionMetadataId: currentFormVersionMetadataId,
                formSectionMetadataId: currentFormSectionMetadataId,
                formFieldMetadataId: currentFormFieldMetadataId,
                formFieldValueMetadataId: currentFormFieldValueMetadataId,
                enabled: currentEnabled, 
                linked: oldLinked,
                ...currentValues 
            } = currentValue;

            // Create a new item with the unique fields.
            const newValue: any = { 
                __type: currentType, 
                enteredBy: currentEnteredBy,
                enteredDate: currentEnteredDate,
                modifiedBy: currentModifiedBy,
                modifiedDate: currentModifiedDate,
                sourceDataConfigurationId: currentConfigurationId,
                sourceDataConfigurationEventId: currentEventId,
                sourceDataConfigurationFormId: currentFormId,
                sourceDataConfigurationFormVersionId: currentVersionId,
                sourceDataConfigurationFormSectionId: currentSectionId,
                sourceDataConfigurationFormFieldId: currentFieldId,
                eventMetadataId: currentEventMetadataId,
                formMetadataId: currentFormMetadataId,
                formVersionMetadataId: currentFormVersionMetadataId,
                formSectionMetadataId: currentFormSectionMetadataId,
                formFieldMetadataId: currentFormFieldMetadataId,
                formFieldValueMetadataId: currentFormFieldValueMetadataId,
                enabled: currentEnabled, 
                linked: newLinked
            }
            

            // If item was linked to the trial configuration copy the trial configuration otherwise copy global configuration.
            const copyValue = data.usingTrialConfiguration ? 
                getFieldValue<any>(trialConfigurationPropertyPath) : 
                getFieldValue<any>(globalConfigurationPropertyPath);

            // remove all the unique details from the value to be copied.
            const { 
                __type: copyType, 
                enteredBy: copyEnteredBy,
                enteredDate: copyEnteredDate,
                modifiedBy: copyModifiedBy,
                modifiedDate: copyModifiedDate,
                sourceDataConfigurationId: copyConfigurationId,
                sourceDataConfigurationEventId: copyEventId,
                sourceDataConfigurationFormId: copyFormId,
                sourceDataConfigurationFormVersionId: copyVersionId,
                sourceDataConfigurationFormSectionId: copySectionId,
                sourceDataConfigurationFormFieldId: copyFieldId,
                eventMetadataId: copyEventMetadataId,
                formMetadataId: copyFormMetadataId,
                formVersionMetadataId: copyFormVersionMetadataId,
                formSectionMetadataId: copyFormSectionMetadataId,
                formFieldMetadataId: copyFormFieldMetadataId,
                formFieldValueMetadataId: copyFormFieldValueMetadataId,
                enabled: copyEnabled,   
                linked: copyLinked, 
                ...copyValues // These are the values we want.
            } = copyValue;

            // loop through and assign values.
            for (let key in copyValues) {
                if (linkedProperties?.some(x => x === key)) {
                    continue;
                }

                newValue[key] = cloneDeep(copyValues[key]);
            }
            
            //if there are linked properties (properties with child items) loop through and update them.
            if (!!linkedProperties) {
                for (let key of linkedProperties) {
                    newValue[key] = {};

                    // if global or event item do not update children (trial items already excluded by condition above)
                    if (item?.isGlobalConfiguration || item?.type === ConfigurationType.Event) {
                        newValue[key] = currentValue[key];
                        continue;
                    }

                    // As item is not global / event or trial item, the children should be empty (since the previously linked local item does not have children).
                    // loop through and create new children.
                    const globalValue = getFieldValue<any>(globalConfigurationPropertyPath);

                    for (let id in globalValue[key]) {
                        newValue[key][id] = {
                            __type: globalValue[key][id].__type,
                            enabled: globalValue[key][id].enabled,
                            linked: true
                        }
                    }
                }
            }

            // update value;
            setFieldValue(basePropertyPath, newValue);
        }

        
        // if the item is now linked and this is not the trail configuration being edited.
        if (newLinked && !item?.isTrialConfiguration) {
            //Get the unique fields from the current item.
            const { 
                __type: currentType, 
                enteredBy: currentEnteredBy,
                enteredDate: currentEnteredDate,
                modifiedBy: currentModifiedBy,
                modifiedDate: currentModifiedDate,
                sourceDataConfigurationId: currentConfigurationId,
                sourceDataConfigurationEventId: currentEventId,
                sourceDataConfigurationFormId: currentFormId,
                sourceDataConfigurationFormVersionId: currentVersionId,
                sourceDataConfigurationFormSectionId: currentSectionId,
                sourceDataConfigurationFormFieldId: currentFieldId,
                eventMetadataId: currentEventMetadataId,
                formMetadataId: currentFormMetadataId,
                formVersionMetadataId: currentFormVersionMetadataId,
                formSectionMetadataId: currentFormSectionMetadataId,
                formFieldMetadataId: currentFormFieldMetadataId,
                formFieldValueMetadataId: currentFormFieldValueMetadataId,
                enabled: currentEnabled, 
                linked: oldLinked,
                ...currentValues 
            } = currentValue;

            // Create a new item with the unique fields.
            const newValue: any = { 
                __type: currentType, 
                enteredBy: currentEnteredBy,
                enteredDate: currentEnteredDate,
                modifiedBy: currentModifiedBy,
                modifiedDate: currentModifiedDate,
                sourceDataConfigurationId: currentConfigurationId,
                sourceDataConfigurationEventId: currentEventId,
                sourceDataConfigurationFormId: currentFormId,
                sourceDataConfigurationFormVersionId: currentVersionId,
                sourceDataConfigurationFormSectionId: currentSectionId,
                sourceDataConfigurationFormFieldId: currentFieldId,
                eventMetadataId: currentEventMetadataId,
                formMetadataId: currentFormMetadataId,
                formVersionMetadataId: currentFormVersionMetadataId,
                formSectionMetadataId: currentFormSectionMetadataId,
                formFieldMetadataId: currentFormFieldMetadataId,
                formFieldValueMetadataId: currentFormFieldValueMetadataId,
                enabled: currentEnabled, 
                linked: newLinked
            }

            
            // if global or event item do not remove children when linking (trial items already excluded by condition above)
            if (item?.isGlobalConfiguration || item?.type === ConfigurationType.Event) {
                if (!!linkedProperties) {
                    // loop through and copy children back to new value.
                    for (let key of linkedProperties) {
                        newValue[key] = currentValue[key];
                    }
                }
            }

            // update value;
            setFieldValue(basePropertyPath, newValue);
        }

        if (!item?.isGlobalConfiguration && item?.type !== ConfigurationType.Event) {
            startLinking(() => {
                setExpanded(false, true);
            })
        }

        return newLinked;
    };

    return (
        <Container
            // @ts-ignore
            spacing={spacing}
        >
            <Divider />
            <ListItem>
                <ListItemText
                    primary={
                        <FlexBox>
                            { 
                                !!enableable && (
                                    <InputOnlyField
                                        propertyPath={`${basePropertyPath}.enabled`}
                                        component={Checkbox}
                                        FormControlProps={{
                                            fullWidth: false
                                        }}
                                    />
                                )
                            }
                            <Tooltip
                                title={type}
                            >
                                <IconBox>
                                    <FontAwesomeIcon fixedWidth icon={typeIcon} />
                                </IconBox>
                            </Tooltip>
                            {
                                overrideName ? 
                                    <Tooltip
                                        title={title}
                                    >
                                        <em>
                                            {overrideName}
                                        </em>
                                    </Tooltip> : 
                                    title
                            }
                            {
                                !!description && (
                                    <DescriptionChip 
                                        label={description}
                                        size="small"
                                        color="primary"
                                    />
                                )
                            }
                            {
                                !!linkable && (
                                    <LinkButton
                                        disabled={isLinking}
                                        // @ts-ignore: used for styling
                                        auto={!description ? "1" : undefined}
                                        color="info"
                                        onClick={link}
                                    >
                                        <IconStack>
                                            {/* {
                                                isLinking && (
                                                    
                                                    <IconStacked>
                                                        <CircularProgress sx={{ml: '1px'}} size="1em" />
                                                    </IconStacked>
                                                )
                                            } */}
                                            {
                                                !linked && (
                                                    <IconStacked>
                                                        <FontAwesomeIcon icon={ faSlash} fixedWidth />
                                                    </IconStacked>
                                                )
                                            }
                                            <IconBase
                                                //@ts-ignore
                                                linked={linked ? "1" : undefined}
                                            >
                                                <FontAwesomeIcon 
                                                    icon={
                                                        !data.usingTrialConfiguration && !data.isGlobalLinked && !item?.isGlobalConfiguration ?
                                                            faLinkHorizontal :
                                                            faEarthOceania
                                                    } 
                                                    fixedWidth 
                                                />
                                            </IconBase>
                                        </IconStack>
                                    </LinkButton>
                                )
                            }
                            {
                                !!edit && (
                                    <EditButton
                                        // @ts-ignore: used for styling
                                        auto={!linkable && !description ? "1" : undefined}
                                        color="info"
                                        onClick={() => {
                                            editContextObserver.set(!!item ? 
                                                {
                                                    item,
                                                    configurationPropertyPath: data.configurationPropetyPath,
                                                    usingGlobalConfiguration: data.usingGlobalConfiguration,
                                                    usingTrialConfiguration: data.usingTrialConfiguration,
                                                    configurationLocalPropertyPath: basePropertyPath,
                                                    configurationGlobalPropertyPath: globalConfigurationPropertyPath,
                                                    configurationTrialPropertyPath: trialConfigurationPropertyPath,
                                                    link: link
                                                } : 
                                                undefined
                                            )
                                        }}
                                    >
                                        <FontAwesomeIcon icon={data.usingTrialConfiguration && !item?.isTrialConfiguration ? faEye : faEdit} fixedWidth />
                                    </EditButton>
                                )
                            }
                            {
                                !!expandable && data.canExpand && (
                                    <ExpandButton 
                                        onClick={() => toggle()}
                                        // @ts-ignore: used for styling
                                        auto={!linkable && !edit && !view && !description ? "1" : undefined}
                                        // @ts-ignore: used for styling
                                        expanded={expanded ? "1" : undefined}
                                    >
                                        <FontAwesomeIcon icon={faChevronDown} fixedWidth />
                                    </ExpandButton>
                                )
                            }
                        </FlexBox>
                    }
                    // secondary={
                    //     <>
                    //         <Box>Is Global Configuration: {item?.isGlobalConfiguration ? 'Yes' : 'No'}</Box>
                    //         <Box>Is Trial Configuration: {item?.isTrialConfiguration ? 'Yes' : 'No'}</Box>
                    //         <Box>Local: {basePropertyPath}</Box>
                    //         <Box>Global: {globalConfigurationPropertyPath}</Box>
                    //         <Box>Trial: {trialConfigurationPropertyPath}</Box>
                    //         <Box>Using: {data.usingTrialConfiguration ? 'Trial' : data.usingGlobalConfiguration ? 'Global' : 'Local'}</Box>
                    //         <Box>Is Global Linked: {data.isGlobalLinked ? 'Yes' : 'No'}</Box>
                    //         <Box>Can Expand: {data.canExpand ? 'Yes' : 'No'}</Box>
                    //     </>
                    // }
                />
            </ListItem>
            <Divider />
        </Container>
    );
}

export default ConfigurationItem;
